import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import StrategyEditor from '@/components/strategy/StrategyEditor'
import StrategiesSidebar from '@/components/strategy/StrategiesSidebar'
import { useMapLabel } from '@/components/strategy/MapSelector'
import { editorIconButton } from '@/components/strategy/editorUi'
import {
  isStrategyKind, sectionFromSlug, sectionPath, strategyEditorPath, type StrategyKind,
} from '@/lib/strategyKinds'
import { useLanguage } from '@/contexts/LanguageContext'

interface StrategyDetail {
  id: number
  title: string
  kind: StrategyKind
  map_name: string
  match_event_id: string
  parent_id: number | null
  created_by: string
  created_by_name: string
  created_at: number
  updated_at: number
  canvas_json: string
  /** Backend'in kararı: rol + sahiplik orada birleşir, burada tekrar edilmez. */
  can_edit: boolean
  /** Kök satırda ilk slaytın id'si; slayt satırında her zaman null. */
  first_slide_id: number | null
}

export default function StrategyMap() {
  const { mapName, section } = useParams<{ mapName: string; section?: string }>()
  const navigate = useNavigate()
  const { T } = useLanguage()
  const [searchParams, setSearchParams] = useSearchParams()

  // Bölüm adresten okunur; tanınmayan slug maç stratejilerine düşer.
  const sec = sectionFromSlug(section)
  const kind: StrategyKind = isStrategyKind(sec) ? sec : 'match'
  const homePath = sectionPath(kind)

  const [selected, setSelected] = useState<number | null>(null)
  const [detail, setDetail] = useState<StrategyDetail | null>(null)
  /** Link bir stratejiyi adlandırdı ama o strateji yok: sessizce boş açma. */
  const [missing, setMissing] = useState(false)
  const [parentStrategyTitle, setParentStrategyTitle] = useState<string | null>(null)
  /** Maç bağı kökte durur; slayt açıkken de oradan okunur. */
  const [matchEventId, setMatchEventId] = useState('')
  const [loadingDetail, setLoadingDetail] = useState(false)
  // Dar ekranda varsayılan kapalı: w-72 kenar çubuğu açıkken telefonda tuvale
  // yer kalmaz. md altında kenar çubuğu tuvalin üstüne bir katman olarak açılır.
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window === 'undefined' ? true : window.innerWidth >= 768)
  // topbarSlot: StrategyEditor'ün topbar'ını portal ettiği DOM düğümü
  const [topbarSlot, setTopbarSlot] = useState<HTMLDivElement | null>(null)

  const label = useMapLabel(mapName ?? '') || T.strategy.map

  /** Son istenen strateji. Art arda seçimde geç gelen eski yanıt yeniyi ezmesin. */
  const wantedRef = useRef<number | null>(null)

  const idParam = searchParams.get('strategy')

  /** `?strategy=` olmadan bu sayfa: hiçbir şey açık değil. */
  const clearStrategyParam = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('strategy')
    setSearchParams(next, { replace: true })
  }

  /**
   * **Adres tek kaynaktır**: hangi slaytın açık olduğunu o söyler, yükleme de
   * yalnızca buradan olur. Bütün kapılar (kitaplık kartı, kenar çubuğu, DM
   * linki, harita değiştirme) adrese yazar; hiçbiri ekranı doğrudan
   * değiştirmez, yoksa adres ile ekran ayrı ayrı yürür.
   *
   * Parametre eskiden okunur okunmaz **siliniyordu**, yani DM'deki link tek
   * kullanımlıktı: yenileyen ya da adresi paylaşan boş bir editöre düşüyordu.
   * Aynı kopukluk haritalar arası geçişte de vardı — adres yeni haritaya
   * gidiyor, ekranda eski strateji kalıyordu.
   */
  useEffect(() => {
    const id = idParam ? parseInt(idParam, 10) : NaN
    if (isNaN(id)) {
      wantedRef.current = null
      setSelected(null)
      setDetail(null)
      setMissing(false)
      setParentStrategyTitle(null)
      setMatchEventId('')
      return
    }
    if (id === selected) return
    void handleSelect(id)
  }, [idParam, mapName, kind]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDetail = async (id: number): Promise<StrategyDetail | null> => {
    try {
      const res = await fetch(`/api/strategies/${id}`, { credentials: 'include' })
      return res.ok ? await res.json() : null
    } catch { return null }
  }

  /**
   * Bir strateji açıldığında ekrana gelen şey **ilk slayttır**.
   *
   * Kök satır bir kapsayıcıdır: ad, harita ve maç bağı onda durur, çizim
   * slaytlarda. Kökün kendi tuvaline düşmek, kenar çubuğunda slaytlar
   * dururken boş bir tuval açmak demekti. Kural tek yerde — burada — durduğu
   * için her kapı aynı davranır: kitaplık kartı, kenar çubuğu ve `?strategy=`
   * ile gelen doğrudan link.
   *
   * Yeni strateji de kural gereği slaytsız kalmaz; ilk slaytı açılışta
   * yaratılır (bkz. StrategyLibrary → CreatePanel). Slaytı olmayan eski bir
   * kök yine kendisi açılır: açılacak başka bir şey yok.
   */
  const handleSelect = async (id: number) => {
    wantedRef.current = id
    setSelected(id)
    setLoadingDetail(true)
    setMissing(false)
    setParentStrategyTitle(null)

    let d = await fetchDetail(id)
    if (wantedRef.current !== id) return
    setMatchEventId(d?.match_event_id ?? '')

    if (d && d.parent_id === null && d.first_slide_id) {
      const slide = await fetchDetail(d.first_slide_id)
      if (wantedRef.current !== id) return
      if (slide) {
        setParentStrategyTitle(d.title)
        setSelected(slide.id)
        d = slide
      }
    } else if (d && d.parent_id) {
      // Açılan bir slayt: kırıntı için kökün adı lazım.
      const parent = await fetchDetail(d.parent_id)
      if (wantedRef.current !== id) return
      if (parent) {
        setParentStrategyTitle(parent.title)
        setMatchEventId(parent.match_event_id)
      }
    }

    // Adresin bölümü ve haritası stratejinin kendisinden gelmeli. Eski ya da
    // elle yazılmış bir link ikisini kaçırabilir; o zaman kırıntı Zestafona
    // derken Bakurani çizilir ya da bir defter planı maç bölümünün içinde
    // açılır. Yetki zaten backend'de, yanlış olan çerçeve — onu düzelt.
    // Adres düzeltilir ama yükleme burada biter: bu sayfa yeniden kurulmaz,
    // dolayısıyla yarıda bırakılan bir açılış sonsuza dek iskelet gösterirdi.
    if (d && (d.map_name !== mapName || d.kind !== kind)) {
      navigate(strategyEditorPath(d.kind, d.map_name, d.id), { replace: true })
    }

    if (d) setDetail(d)
    else setMissing(true)
    setLoadingDetail(false)
  }

  // Kenar çubuğu da adrese yazar: aynı harita ya da başka harita, tek yol.
  const handleDrawerSelect = (incomingMapName: string, id: number) => {
    navigate(strategyEditorPath(kind, incomingMapName, id), { replace: true })
  }

  const handleDelete = (id: number) => {
    // Adresi temizle, gerisini yukarıdaki etki yapsın: silinen stratejinin
    // id'si adreste kalsaydı yenileme onu "artık burada değil" diye açardı.
    if (selected === id) clearStrategyParam()
  }

  const handleRename = (id: number, newTitle: string) => {
    if (detail && detail.id === id) {
      setDetail(prev => prev ? { ...prev, title: newTitle } : prev)
    }
    if (detail && detail.parent_id === id) {
      setParentStrategyTitle(newTitle)
    }
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden">

      {/* ── Tam genişlik topbar yuvası ──────────────────────────────── */}
      {/* Bir strateji yüklendiğinde StrategyEditor topbar'ını buraya portal eder */}
      {detail ? (
        <div ref={el => { if (el && el !== topbarSlot) setTopbarSlot(el) }} className="shrink-0" />
      ) : (
        /* Strateji açılmadan önceki üst çubuk. Editörünkiyle **aynı ölçü ve
           yüzey** (68px, panelin camı): bir strateji seçildiğinde çubuğun
           yerini editörünki alıyor, ikisi farklı yükseklikte olsaydı sayfa
           seçim anında zıplardı. */
        <div className="flex h-[68px] shrink-0 items-center gap-3 border-b border-white/10 bg-black/25 px-4 backdrop-blur-sm">
          <button
            onClick={() => navigate(homePath)}
            className={editorIconButton()}
            title={T.strategy.home}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span aria-hidden className="h-px w-4 shrink-0 bg-primary" />
          <span className="font-display text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/70">
            {kind === 'match' ? T.strategy.tabs.match : T.strategy.tabs.playbook}
          </span>
          <span className="text-sm text-muted-foreground/30">/</span>
          {/* Harita adı veridir, büyütülmez. */}
          <span className="truncate font-display text-sm tracking-[0.04em] text-muted-foreground">{label}</span>
        </div>
      )}

      {/* ── Topbar'ın altı: kenar çubuğu + içerik ─────────────────── */}
      <div className="relative flex flex-1 overflow-hidden">

        {/* Mobil örtü — kenar çubuğu açıkken tuvalin üstüne iner, tıklanınca kapar */}
        {sidebarOpen && (
          <div
            className="absolute inset-0 z-10 bg-black/50 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sol kenar çubuğu — md altında tuvalin üstünde katman, md+ akışta */}
        <div className={`absolute inset-y-0 left-0 z-20 shrink-0 overflow-hidden transition-all duration-300 ease-in-out border-r border-white/10 md:static md:z-auto ${sidebarOpen ? 'w-72' : 'w-0'}`}>
          <div className="flex h-full w-72 flex-col bg-black/25 backdrop-blur-sm">
            <StrategiesSidebar
              kind={kind}
              activeStrategyId={selected}
              onSelectStrategy={handleDrawerSelect}
              onDeleteStrategy={handleDelete}
              onRenameStrategy={handleRename}
            />
          </div>
        </div>

        {/* Açma/kapama sekmesi */}
        <div className="flex items-center shrink-0">
          <button
            onClick={() => setSidebarOpen(o => !o)}
            className="flex h-16 w-5 cursor-pointer items-center justify-center rounded-r-sm border-y border-r border-white/10 bg-black/40 text-muted-foreground backdrop-blur-sm transition-colors hover:bg-white/10 hover:text-foreground"
            title={sidebarOpen ? T.strategy.sidebarClose : T.strategy.sidebarOpen}
          >
            <ChevronLeft className={`h-3.5 w-3.5 transition-transform duration-300 ${sidebarOpen ? '' : 'rotate-180'}`} />
          </button>
        </div>

        {/* Editör / harita paneli */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {loadingDetail ? (
            <div className="flex h-full items-center justify-center">
              <div className="animate-pulse space-y-3 text-center">
                <div className="mx-auto h-10 w-10 rounded-sm bg-white/8" />
                <div className="mx-auto h-3 w-24 rounded-sm bg-white/6" />
              </div>
            </div>
          ) : detail ? (
            <StrategyEditor
              key={detail.id}
              strategyId={detail.id}
              initialTitle={detail.title}
              initialMapName={detail.map_name}
              initialCanvasJson={detail.canvas_json}
              parentStrategyTitle={parentStrategyTitle}
              matchEventId={matchEventId}
              canEdit={detail.can_edit}
              onTitleChange={() => {}}
              onNavigateHome={() => navigate(homePath)}
              renderTopbar={topbar => topbarSlot ? createPortal(topbar, topbarSlot) : null}
            />
          ) : (
            <div className="flex-1 overflow-hidden bg-background flex flex-col items-center justify-end pb-8">
              {/* Bir link strateji adlandırıp bulamadıysa bunu söyle: fısıltı
                  hâlindeki "kenar çubuğundan seç", link hiç bir şey taşımamış
                  gibi okunuyor. */}
              {missing ? (
                <div className="max-w-xs space-y-1 text-center">
                  <p className="text-sm text-foreground/80">{T.strategy.gone}</p>
                  <p className="text-xs text-muted-foreground">{T.strategy.goneHint}</p>
                </div>
              ) : (
                <p className="text-xs text-white/25 select-none">
                  {T.strategy.pickFromSidebar}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
